import React from 'react';
import { connect } from 'react-redux';
import { camViewChange, setSingleView, setCameraBrowserCam } from '../actions';

import '../App.css';
import './liveStyle.css';

/**
 * CamButtonsView - Camera set/bank selector (redesign #86bb5dx62)
 *
 * Renders the camera-bank strip: a centered, horizontally-scrolling row of
 * set buttons (single cams for conf-1, page ranges "1-4"/"5-8"… for multi-cam)
 * flanked by ‹ › scroll arrows on mobile. On desktop the same strip shows a
 * scrollbar and the pinned Auto-Scan (AS) button instead of the arrows.
 * Arrows only appear when the strip actually overflows.
 */
class CamButtonsView extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            canScroll: false   // strip overflows its container → show ‹ › arrows (mobile)
        };
        this.scrollRef = React.createRef();
    }

    componentDidMount() {
        this.checkOverflow();
        window.addEventListener('resize', this.checkOverflow);
    }

    componentDidUpdate() {
        // Re-measure when the button set changes (conf switch, cam list load, selection)
        this.checkOverflow();
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.checkOverflow);
    }

    /** Toggle the ‹ › arrows based on whether the strip overflows. */
    checkOverflow = () => {
        const el = this.scrollRef.current;
        if (!el) return;
        const canScroll = el.scrollWidth > el.clientWidth + 4;
        if (canScroll !== this.state.canScroll) {
            this.setState({ canScroll });
        }
    }

    /** Scroll the bank strip left/right by roughly one viewport chunk. */
    scrollStrip = (dir) => {
        const el = this.scrollRef.current;
        if (!el) return;
        el.scrollBy({ left: dir * Math.max(120, Math.round(el.clientWidth * 0.7)), behavior: 'smooth' });
    }

    /**
     * Get button class based on camera state
     * @param {boolean} isEnabled - Camera is enabled
     * @param {boolean} hasAccess - User has access to camera
     * @param {boolean} isSelected - Camera is currently selected
     */
    getCamBtnClass = (isEnabled, hasAccess, isSelected) => {
        if (!isEnabled || !hasAccess) {
            return 'live-cam-btn live-cam-btn--disabled';
        }
        if (isSelected) {
            return 'live-cam-btn live-cam-btn--active hoverableButton';
        }
        return 'live-cam-btn live-cam-btn--default hoverableButton';
    }

    /**
     * Get auto-scan button class
     * @param {boolean} isScanning - Auto-scan is active
     */
    getAutoScanClass = (isScanning) => {
        const base = 'live-cam-btn live-cam-btn--autoscan hoverable';
        return isScanning ? `${base} live-cam-btn--autoscan-active` : base;
    }

    // c[0] - string cam_x
    // cam[1] - cam number - int
    // c[2] - enabled?
    setSingleCamData = cam => {
        this.props.camViewChange(cam[0]);
        this.props.setCameraBrowserCam(cam[1]);
    }

    setMultipleCamData = cam => {
        this.props.singleCamSelected && this.props.setSingleView('');
        this.props.camViewChange(cam);
    }

    /**
     * Render a single-camera button (conf-1)
     */
    renderCamButton = (c) => {
        const isEnabled = c[2];
        const hasAccess = this.props.userCameras.indexOf(parseInt(c[1])) > -1;
        const isSelected = this.props.currentCamView === c[0];
        const canClick = isEnabled && hasAccess;

        return (
            <button
                key={c[1]}
                type="button"
                className={this.getCamBtnClass(isEnabled, hasAccess, isSelected)}
                onClick={() => canClick ? this.setSingleCamData(c) : null}>
                {c[1]}
            </button>
        );
    }

    /**
     * Render a multi-cam set/bank button (quad, six, nine, twelve, sixteen).
     * v[0] is a "1-4" range string, v[1] is enabled.
     */
    renderMultiViewBtn = (v) => {
        const isEnabled = v[1];
        const isSelected = this.props.currentCamView === "cam_" + v[0];
        const btnClass = isEnabled
            ? (isSelected ? 'live-cam-btn live-cam-btn--active hoverable' : 'live-cam-btn live-cam-btn--default hoverable')
            : 'live-cam-btn live-cam-btn--disabled';

        return (
            <button
                key={v[0]}
                type="button"
                className={btnClass}
                onClick={() => isEnabled ? this.setMultipleCamData('cam_' + v[0]) : null}>
                {v[0]}
            </button>
        );
    }

    /** Pick the button list for the current configuration. Only show sets we can actually FILL — drop the
        empty banks entirely instead of dimming them (David 2026-08-04). SHARED by Live + playback (the
        reused set strip), so both look identical. Non-contiguous enabled banks are preserved (bank 1,
        empty bank 2, bank 3 → show 1 + 3). */
    renderButtons = () => {
        const cams = (arr) => (arr || []).filter(c => c && c[2]);
        const sets = (arr) => (arr || []).filter(v => v && v[1]);
        switch (this.props.conf) {
            case 'conf-1':  return cams(this.props.cameraArr).map(c => this.renderCamButton(c));
            // twoView/eightView are NEW (#86bb5dx62): a returning user's persisted
            // `server` slice (redux-persist hard-set) won't have them until the next
            // SET_ALL_CAMERAS, so guard against undefined during the rehydrate window.
            case 'conf-2':  return sets(this.props.twoView).map(v => this.renderMultiViewBtn(v));
            case 'conf-4':  return sets(this.props.quadView).map(v => this.renderMultiViewBtn(v));
            case 'conf-6':  return sets(this.props.sixView).map(v => this.renderMultiViewBtn(v));
            case 'conf-8':  return sets(this.props.eightView).map(v => this.renderMultiViewBtn(v));
            case 'conf-9':  return sets(this.props.nineView).map(v => this.renderMultiViewBtn(v));
            case 'conf-12': return sets(this.props.twelveView).map(v => this.renderMultiViewBtn(v));
            case 'conf-16': return sets(this.props.sixteenView).map(v => this.renderMultiViewBtn(v));
            default:        return null;
        }
    }

    render() {
        return (
            <div className="live-cam-buttons-row">
                { this.state.canScroll &&
                    <button
                        type="button"
                        className="live-cam-scroll-arrow live-cam-scroll-arrow--prev"
                        onClick={() => this.scrollStrip(-1)}
                        aria-label="Previous cameras">‹</button>
                }

                <div className="live-cam-buttons-scroll" ref={this.scrollRef}>
                    {this.renderButtons()}
                </div>

                { this.state.canScroll &&
                    <button
                        type="button"
                        className="live-cam-scroll-arrow live-cam-scroll-arrow--next"
                        onClick={() => this.scrollStrip(1)}
                        aria-label="Next cameras">›</button>
                }

                {/* hideAutoScan (desktop bottom bar, #86bb5dx62): the redesigned desktop bottom
                    bar has its own labeled "Scan" button on the left, so the banks strip drops the
                    pinned AS button. Default (mobile/tablet + old desktop) keeps it. */}
                { !this.props.hideAutoScan &&
                    <button
                        type="button"
                        className={this.getAutoScanClass(this.props.autoScanning)}
                        onClick={this.props.toggleAutoScan}
                        title="Auto Scroll">
                        AS
                    </button>
                }
            </div>
        );
    }
}

const mapStateToProps = state => {
    const { conf } = state.config;
    const {
        bAutoScanTimeout,
        bNumCams,
        cameras,
        cameraArr,
        singleViewEnabledArr,
        twoView,
        twoViewEnabledArr,
        quadView,
        quadViewEnabledArr,
        sixView,
        sixViewEnabledArr,
        eightView,
        eightViewEnabledArr,
        nineView,
        nineViewEnabledArr,
        twelveView,
        twelveViewEnabledArr,
        sixteenView,
        sixteenViewEnabledArr
     } = state.server;
    const { currentCamView, autoScrollEnabled } = state.camera;
    const { singleCamSelected } = state.video;
    const { userCameras } = state.user;
    return {
        conf,
        currentCamView,
        autoScrollEnabled,
        bAutoScanTimeout,
        bNumCams,
        cameras,
        cameraArr,
        singleViewEnabledArr,
        twoView,
        twoViewEnabledArr,
        quadView,
        quadViewEnabledArr,
        sixView,
        sixViewEnabledArr,
        eightView,
        eightViewEnabledArr,
        nineView,
        nineViewEnabledArr,
        twelveView,
        twelveViewEnabledArr,
        sixteenView,
        sixteenViewEnabledArr,
        singleCamSelected,
        userCameras
    }
}

export default connect(mapStateToProps, { camViewChange, setSingleView, setCameraBrowserCam })(CamButtonsView);
